
import React, { createContext, useState, useEffect, useRef } from 'react';
import anime, { set } from "animejs";
import minoLayers from '../data/minoLayers';
import skierLayers from '../data/skierLayers';
import skierLayersSVG from '../data/skierLayersSVG';
import houseCleaningLayers from "../data/houseCleaningLayers";
import babyLayers from "../data/babyLayers";
import sweetHomeLayers from "../data/sweetHomeLayers";

export const CanvasContext = createContext();

export const CanvasProvider = ({ children }) => {

  const API_URL = process.env.REACT_APP_API_URL;

  const [activeLayers, setActiveLayers] = useState([]);
  const [animationBlocks, setAnimationBlocks] = useState({});
  const [animationScript, setAnimationScript] = useState('');
  const [concept, setConcept] = useState([]);
  const [dragAction, setDragAction] = useState(false);
  const [elementOffsets, setElementOffsets] = useState({});
  const [elementsInvolvedByRole, setElementsInvolvedByRole] = useState({});
  const [groups, setGroups] = useState({});
  const [initiateDragDrop, setInitiateDragDrop] = useState(false);
  const [isPlayingAnimation, setIsPlayingAnimation] = useState(false);
  const [jsonData, setJsonData] = useState({});
  const [layers, setLayers] = useState([]);
  const [layersLoading, setLayersLoading] = useState(false);
  const [layoutType, setLayoutType] = useState([]);
  const [loading, setLoading] = useState(false);
  const [llmType, setLlmType] = useState("gpt"); // Default value
  const [overlayMessage, setOverlayMessage] = useState('');
  const [primaryElementOrientation, setPrimaryElementOrientation] = useState('right');
  const [queryLayoutType, setQueryLayoutType] = useState('');
  const [selectedAnimation, setSelectedAnimation] = useState(null);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [enrichedSelectedSegments, setEnrichedSelectedSegments] = useState([]);
  const [showScriptPanel, setShowScriptPanel] = useState(false);
  const [showTextPanel, setShowTextPanel] = React.useState(false);
  const [suggestionOne, setSuggestionOne] = useState('');
  const [suggestionTwo, setSuggestionTwo] = useState('');
  const [treeData, setTreeData] = useState({});

  // Function to highlight canvas elements based on selected segments
  const highlightCanvasElements = (selectedSegments) => {
    console.log('[highlightCanvasElements] called with selectedSegments:', selectedSegments);

    // First, remove all existing highlights
    layers.forEach(layer => {
      const element = document.getElementById(layer.id);
      if (element) {
        element.style.outline = '';
        element.style.boxShadow = '';
        element.style.filter = '';
      }
    });

    // If no segments selected, return early
    if (!selectedSegments || selectedSegments.length === 0) {
      console.log('[highlightCanvasElements] No segments selected, returning early.');
      return;
    }

    // Collect all target IDs from selected segments
    const targetIds = new Set();
    selectedSegments.forEach(segment => {
      if (segment.targets && Array.isArray(segment.targets)) {
        segment.targets.forEach(target => {
          let targetId;
          if (typeof target === 'string') {
            targetId = target.startsWith('#') ? target.substring(1) : target;
          } else if (target && target.id) {
            targetId = target.id;
          }
          if (targetId) {
            targetIds.add(targetId);
          }
        });
      }
    });
    console.log('[highlightCanvasElements] Computed targetIds:', Array.from(targetIds));

    // Apply highlight styles to target elements
    targetIds.forEach(targetId => {
      const element = document.getElementById(targetId);
      if (element) {
        console.log(`[highlightCanvasElements] Highlighting element with id: ${targetId}`);
        element.style.outline = '3px solid #ff6b35';
        element.style.boxShadow = '0 0 15px rgba(255, 107, 53, 0.6)';
        element.style.filter = 'brightness(1.1)';
        element.style.transition = 'all 0.3s ease';
      } else {
        console.warn(`[highlightCanvasElements] No element found with id: ${targetId}`);
      }
    });
  };

  const playAnimation = async (animationCode, filename) => {
    try {
      setLoading(false);
      resetCanvasElementsForReplay();
      setAnimationScript(animationCode);
      await executeAnimation(anime, animationCode, filename, true); // Await the animation execution
    } catch (error) {
      console.error('Failed to start animation:', error);
    }
  };

const generateGroups = (layers) => {
  console.log(layers);

  for (let i = 0; i < layers.length; i++) {
    console.log(layers[i].role);
  }

  const groupedLayers = layers.reduce((acc, layer) => {
    const { role, group } = layer;
    if (!acc[role]) {
      acc[role] = {};
    }
    const groupKey = group;
    console.log(groupKey);
    if (!acc[role][groupKey]) {
      acc[role][groupKey] = [];
    }
    acc[role][groupKey].push(layer);
    return acc;
  }, {});

  console.log(groupedLayers["primary"]);

  setPrimaryElementOrientation(groupedLayers["primary"]["0"][0].orientation);

  setGroups(groupedLayers); // Update the state
};


const generateEdit = async (textFieldValue) => {
  setElementOffsets({});
  const userId = localStorage.getItem('userId');

  setLoading(true);

  const layerStates = layers.map(layer => {
      const element = document.getElementById(layer.id);
      const transformValues = getComputedTransformValues(element);

      return {
          id: layer.id,
          left: element.style.left,
          top: element.style.top,
          width: element.style.width,
          height: element.style.height,
          zIndex: element.style.zIndex,
          transform: {
              translateX: transformValues.translateX,
              translateY: transformValues.translateY,
              scaleX: transformValues.scaleX,
              scaleY: transformValues.scaleY
          },
          src: layer.src,
          alt: layer.alt,
          role: layer.role,
          group: layer.group
      };
  });

  try {
      const response = await fetch(`${API_URL}/generate_edit_request`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: textFieldValue,
            layers,
            filename: selectedAnimation,
            elementsInvolvedByRole: elementsInvolvedByRole,
            userId,
            llm: llmType,
            layoutType,
            design_concept: concept,
            implemented_code: animationScript
        }),
      });

      

      const { task_id } = await response.json();

      const pollStatus = async () => {
          const statusResponse = await fetch(`${API_URL}/task_status/${task_id}`);
          const statusData = await statusResponse.json();

          if (statusData.state === 'PROGRESS') {
              setOverlayMessage(`${statusData.meta.status}`);
          }

          if (statusData.state === 'SUCCESS') {

              console.log('Task completed:', statusData.result);

              setConcept(statusData.result.concept);
              setLayers(statusData.result.layers);
              setGroups(statusData.result.groups);
              setOverlayMessage(`Animation First Draft `);
              await playAnimation(statusData.result.logo_animation, statusData.result.filename);
              await fetchData();
              setLoading(false);


          } else {
              setTimeout(pollStatus, 2000); // Poll every 2 seconds
          }
      };

      pollStatus();

  } catch (error) {
      console.error('Error suggesting a design concept :', error);
  }
};


const generateAnimation = async () => {
  setElementOffsets({});
  const userId = localStorage.getItem('userId');

  setLoading(true);

  const layerStates = layers.map(layer => {
      const element = document.getElementById(layer.id);
      const transformValues = getComputedTransformValues(element);

      return {
          id: layer.id,
          left: element.style.left,
          top: element.style.top,
          width: element.style.width,
          height: element.style.height,
          zIndex: element.style.zIndex,
          transform: {
              translateX: transformValues.translateX,
              translateY: transformValues.translateY,
              scaleX: transformValues.scaleX,
              scaleY: transformValues.scaleY
          },
          src: layer.src,
          alt: layer.alt,
          role: layer.role,
          group: layer.group
      };
  });

  try {
      const response = await fetch(`${API_URL}/generate_animation`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify({ layers: layerStates, layout_type: layoutType, userId, llm: llmType, orientation: primaryElementOrientation }),
      });

      const { task_id } = await response.json();

      const pollStatus = async () => {
          const statusResponse = await fetch(`${API_URL}/task_status/${task_id}`);
          const statusData = await statusResponse.json();

          if (statusData.state === 'PROGRESS') {
              setOverlayMessage(`${statusData.meta.status}`);
          }

          if (statusData.state === 'SUCCESS') {

              console.log('Task completed:', statusData.result);

              setConcept(statusData.result.concept);
              setLayers(statusData.result.layers);
              setGroups(statusData.result.groups);
              setOverlayMessage(`Animation First Draft `);
              await playAnimation(statusData.result.logo_animation, statusData.result.filename);
              await fetchData();
              setLoading(false);


          } else {
              setTimeout(pollStatus, 2000); // Poll every 2 seconds
          }
      };

      pollStatus();

  } catch (error) {
      console.error('Error suggesting a design concept :', error);
  }
};


  useEffect(() => {

    if (!queryLayoutType) {
      return;
    }

    setLayoutType(queryLayoutType); // Set the layout type based on queryLayoutType
    setLayersLoading(true); // Set loading to true while layers are being updated

    let selectedLayers;
    switch (queryLayoutType) {
      case 'mino':
        selectedLayers = minoLayers;
        break;
      case 'skier':
        selectedLayers = skierLayers;
        
        break;
      case 'skiersvg':
        selectedLayers = skierLayersSVG ; 
        break
      case 'housecleaning':
        selectedLayers = houseCleaningLayers;
        break;
      case 'baby':
        selectedLayers = babyLayers;
        break;
      case 'sweethome':
        selectedLayers = sweetHomeLayers;
        break;
      default:
        selectedLayers = [];
        break;
    }

    setLayers(selectedLayers); // Update the layers state
    setLayersLoading(false);
    generateGroups(selectedLayers);
  }, [queryLayoutType, setLayoutType, setLayers]); // Add dependencies


  const appendCompletionLogic = (script, filename, isInitialPlay) => {
    console.log(filename);

    if (isInitialPlay) {
      return `
          (function(anime) {
            try {
              ${script}

              timeline.finished.then(() => {
                setIsPlayingAnimation(false);
                setOverlayMessage('');
                console.log(elementOffsets, "elementOffsets");

                setElementOffsetsBackend(elementOffsets, '${filename}');
                setElementOffsets(elementOffsets);
              });
              return timeline;
            } catch (error) {
              console.error('Error in animation script:', error);
              return null;
            }
          })(anime);
        `;
  } else {
    return `
          (function(anime) {
            try {
              ${script}

              timeline.finished.then(() => {
                setIsPlayingAnimation(false);
                setOverlayMessage('');
                setElementOffsets(elementOffsets);
              });
              return timeline;
            } catch (error) {
              console.error('Error in animation script:', error);
              return null;
            }
          })(anime);
        `;

  }


  }

    
  const resetCanvasElementsForReplay = async () => {
    layers.forEach(layer => {
      const element = document.getElementById(layer.id);
      if (element) {
        // Reset specific animated properties
        element.style.transform = ''; // Reset transform properties
        element.style.boxShadow = ''; // Reset box-shadow properties
        element.style.opacity = ''; // Reset opacity properties (if animated)
        element.style.transition = ''; // Reset transition properties (if animated)
        element.style.animation = ''; // Reset animation properties (if animated)
        // Reset highlight properties
        element.style.outline = '';
        element.style.filter = '';
      }
    });

    setLayers([...layers]);
  }

  useEffect(() => {
    const enrichSegments = () => {
      const newEnrichedSegments = selectedSegments.map(segment => {
        const altTexts = [];
        if (segment.targets && Array.isArray(segment.targets)) {
          segment.targets.forEach(target => {
            const targetId = typeof target === 'string' ? target : target.id;
            const matchingLayer = layers.find(layer => layer.id === targetId);
            if (matchingLayer) {
              if (matchingLayer.alt) {
                altTexts.push(matchingLayer.alt);
              }
              // Add src to the segment if available
              if (matchingLayer.src) {
                segment.src = matchingLayer.src;
              }
            }
          });
        }
        return {
          ...segment,
          altTexts: altTexts.length > 0 ? altTexts : [segment.animation_event] // Fallback to animation_event if no alt text
        };
      });
      setEnrichedSelectedSegments(newEnrichedSegments);
    };

    enrichSegments();
  }, [selectedSegments, layers]);

  const executeAnimation = (anime, logo_animation, filename, isInitialPlay = false) => {
    return new Promise((resolve, reject) => {

      const executeAnimationCode = (anime, logo_animation, filename) => {

        
        const modifiedScript = appendCompletionLogic(logo_animation, filename, isInitialPlay);
      
        
        let timeline;
        try {
          timeline = eval(modifiedScript, anime);

        } catch (error) {
          console.error('Error executing the animation script:', error);
          reject(error); // Reject the Promise on error
          return;
        }

        if (timeline) {
          timeline.play();

        } else {
          console.error('No timeline returned from the script.', timeline);
          reject('No timeline returned from the script.'); // Reject the Promise if no timeline
        }
      };

      setIsPlayingAnimation(true);
      executeAnimationCode(anime, logo_animation, filename);
    });
  };

  const isSelected = (fileName) => selectedAnimation === fileName;

  const fetchData = async () => {
    const userId = localStorage.getItem('userId');

    try {
      const response = await fetch(`${API_URL}/get_files_firebase?template=${layoutType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
        }),
      });
      const data = await response.json();
      console.log(data["directory_structure"])
      setTreeData({ ...data["directory_structure"] });
      const jsonFiles = findJsonFiles(data["directory_structure"]);
      const jsonDataPromises = jsonFiles.map(filePath => fetchJsonData(filePath));
      const jsonDataResults = await Promise.all(jsonDataPromises);
      const jsonDataMap = jsonFiles.reduce((acc, filePath, index) => {
        acc[filePath] = jsonDataResults[index];
        return acc;
      }, {});
      setJsonData(jsonDataMap);
    } catch (error) {
      console.error("There was an error fetching the files!", error);
    }
  };

  const findJsonFiles = (nodes, path = '') => {
    let jsonFiles = [];
    Object.keys(nodes).forEach(key => {
      const currentPath = path ? `${path}/${key}` : key;
      if (key.endsWith('.json')) {
        jsonFiles.push(currentPath);
      } else if (typeof nodes[key] === 'object') {
        jsonFiles = jsonFiles.concat(findJsonFiles(nodes[key], currentPath));
      }
    });
    return jsonFiles;
  };

  const fetchJsonData = async (filePath) => {
    const userId = localStorage.getItem('userId');

    try {
      const response = await fetch(`${API_URL}/get_animation_json_data_firebase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filePath: filePath, userId: userId }),
      });
      const data = await response.json();
      return data; // Return the fetched data
    } catch (error) {
      console.error(`There was an error fetching the JSON data for ${filePath}!`, error);
      return null; // Return null in case of an error
    }
  };

  // Used in calculate_timeline which is injected and executed dynamically
  const setElementOffsetsBackend = async (elementOffsets, filename) => {
    const userId = localStorage.getItem('userId');
    setLoading(true);

    const response = await fetch(`${API_URL}/set_element_offsets_firebase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ element_offsets: elementOffsets, userId, filename }),
    });

    if (response.ok) {
      const data = await response.json();
      setAnimationBlocks(data.segments);
      console.log("set animation blocks", data.segments);
      setLoading(false);
    }
  }

  const getComputedTransformValues = (element) => {
    try {
      const computedStyle = window.getComputedStyle(element);
      const transform = computedStyle.transform;

      if (transform && transform !== 'none') {
        // transform: matrix(a, b, c, d, tx, ty) -> scaleX = a, scaleY = d, translateX = tx, translateY = ty
        const values = transform.match(/matrix\(([^)]+)\)/);
        if (values && values[1]) {
          const matrixValues = values[1].split(',').map(v => parseFloat(v.trim()));
          const a = matrixValues[0] || 1;
          const b = matrixValues[1] || 0;
          const d = matrixValues[3] || 1;
          const tx = matrixValues[4] || 0;
          const ty = matrixValues[5] || 0;

          // Calculate rotation in degrees
          const rotate = Math.atan2(b, a) * (180 / Math.PI);

          return {
            translateX: tx,
            translateY: ty,
            scaleX: a,
            scaleY: d,
            rotate: rotate,
          };
        }
      }
      return {
        translateX: 0,
        translateY: 0,
        scaleX: 1,
        scaleY: 1,
        rotate: 0,
      };
    } catch (error) {
      console.error('Error processing element:', element, error);
      return {
        translateX: 0,
        translateY: 0,
        scaleX: 1,
        scaleY: 1,
        rotate: 0,
      };
    }
  };
  return (
    <CanvasContext.Provider value={{
      activeLayers, setActiveLayers,
      animationBlocks, setAnimationBlocks,
      animationScript, setAnimationScript,
      API_URL,
      concept, setConcept,
      dragAction, setDragAction,
      elementsInvolvedByRole, setElementsInvolvedByRole,
      elementOffsets, setElementOffsets,
      executeAnimation,
      fetchData,
      getComputedTransformValues,
      groups, setGroups,
      generateAnimation,
      generateEdit,
      generateGroups,
      highlightCanvasElements,
      initiateDragDrop, setInitiateDragDrop,
      isPlayingAnimation, setIsPlayingAnimation,
      isSelected,
      jsonData, setJsonData,
      layers, setLayers,
      layersLoading, setLayersLoading,
      layoutType, setLayoutType,
      loading, setLoading,
      llmType, setLlmType,
      overlayMessage, setOverlayMessage,
      playAnimation,
      primaryElementOrientation, setPrimaryElementOrientation,
      queryLayoutType, setQueryLayoutType,
      selectedAnimation, setSelectedAnimation,
      selectedSegments, setSelectedSegments,
      enrichedSelectedSegments, // Add the new state variable
      setElementOffsetsBackend,
      showScriptPanel, setShowScriptPanel,
      showTextPanel, setShowTextPanel,
      suggestionOne, setSuggestionOne,
      suggestionTwo, setSuggestionTwo,
      treeData, setTreeData,
    }}>
      {children}
    </CanvasContext.Provider>
  );
};
